import React from "react";
import { IFacility } from "@/api/facilities";
import { Button, Col, Form, Modal, Radio, Row, Space } from "antd";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import styles from "./BulkEditScopeModal.module.scss";
import { CalendarOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { UtilityContractGroupInfo } from "@/pages/facilities/detail/Utilities/UtilityContractGroup";
import { useWatch } from "antd/es/form/Form";
import { useBulkPostUsedAllUtilities } from "@/hooks/useFacilities";

function BulkEditScopeModal({
  facilities,
  children,
}: {
  facilities: IFacility[];
  children: React.ReactNode;
}) {
  const [form] = Form.useForm();
  useWatch([], form);

  const [isOpen, setIsOpen] = React.useState(false);
  const bulkPostAllUtilities = useBulkPostUsedAllUtilities();
  const { year } = useDashboardYear();

  const isAllOffInForm = Object.entries(UtilityContractGroupInfo).every(([key]) => {
    return form.getFieldValue(key) === false;
  });
  const isAnyOffInForm = Object.entries(UtilityContractGroupInfo).some(([key]) => {
    return form.getFieldValue(key) === false;
  });

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const postObject = Object.entries(UtilityContractGroupInfo).reduce(
        (acc, [key]) => {
          acc[key] = values[key];
          return acc;
        },
        {} as Record<string, boolean>
      );

      if (year) {
        bulkPostAllUtilities
          .mutateAsync({
            year: year,
            facilityIds: facilities.map((facility) => facility.id),
            postUsed: postObject,
          })
          .then(() => {
            setIsOpen(false);
            form.resetFields();
          });
      }
    });
  };

  const handleSwitchAll = (value: boolean) => {
    Object.keys(UtilityContractGroupInfo).forEach((key) => {
      form.setFieldValue(key, value);
    });
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  return (
    <>
      <span onClick={() => setIsOpen(true)}>{children}</span>
      <Modal
        open={isOpen}
        width={600}
        title={`Edit scope for ${facilities.length} facilit${
          facilities.length > 1 ? "ies" : "y"
        } for ${year}`}
        onCancel={handleCancel}
        onOk={onSubmit}
        okText={"Save"}
        okButtonProps={{ loading: bulkPostAllUtilities.isLoading }}
      >
        <Form
          form={form}
          onKeyUp={(e) => {
            if (e.code === "Enter") onSubmit();
          }}
          layout="vertical"
          name="form_in_modal"
          initialValues={Object.fromEntries(
            Object.keys(UtilityContractGroupInfo).map((key) => [key, true])
          )}
        >
          <Row className={styles.container}>
            <Col span={24}>
              Manage the relevant utilities and operational impact by enabling or disabling them for
              the selected facilities.
            </Col>
            <Col span={24}>
              <Row className={styles.utilitiesContainer}>
                <Col
                  span={24}
                  className={styles.topBar}
                >
                  <Row
                    justify={"space-between"}
                    align={"middle"}
                  >
                    <Col>
                      <Space className={styles.yearContainer}>
                        <CalendarOutlined />
                        <span>{year}</span>
                      </Space>
                    </Col>
                    <Col className={styles.switchContainer}>
                      <Button
                        ghost
                        size={"small"}
                        onClick={() => handleSwitchAll(!!isAllOffInForm)}
                      >
                        Switch all {isAllOffInForm ? "on" : "off"}
                      </Button>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  {Object.entries(UtilityContractGroupInfo).map(([key, value]) => (
                    <Row
                      align={"middle"}
                      justify={"space-between"}
                      className={styles.utilityItem}
                      gutter={[2, 0]}
                    >
                      <Col>
                        <Row
                          align={"middle"}
                          gutter={[8, 0]}
                        >
                          <Col key={key}>{value.icon}</Col>
                          <Col>
                            <span>{value.title}</span>
                          </Col>
                        </Row>
                      </Col>
                      <Col>
                        <Form.Item
                          noStyle
                          name={key}
                        >
                          <Radio.Group
                            block
                            options={[
                              { label: "Off", value: false },
                              { label: "On", value: true },
                            ]}
                            optionType="button"
                            size={"small"}
                            buttonStyle={"solid"}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Row>
            </Col>
            {isAnyOffInForm && (
              <Col
                span={24}
                className={styles.note}
              >
                <Space>
                  <ExclamationCircleFilled className={styles.iconColor} />
                  <span>
                    Utilities or operational variables that have data for the selected year, will
                    remain in scope. Remove the data first to exclude them.
                  </span>
                </Space>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default BulkEditScopeModal;
